import { useEffect, useRef } from 'react';
import {
  useGetCoilDutiesQuery,
  useGetRatedCoilQuery,
} from 'app/services/ahuComponentFormsApi';
import { ahuBoardBus } from '../../bus/ahuBoardBus';
import { COMPONENT_FORM_CONFIGS } from '../componentFormConfigs';
import { useComponentForm } from '../useComponentForm';
import { cmsList, FormSubtab, handleTabToNext, type OptionRow } from '../formKit';
import { formatDecimalDisplay, formatIntegerDisplay } from '../formUtils';
import { useJsonDiffEmit } from '../jsonDiffEmit';
import {
  BlankOffSlot,
  FormHeading,
  MBareInput,
  MBareSelect,
  useAirStreamRowsFor,
  MInput,
  PBareSelect,
  PSelect,
  RequiredFieldNote,
  SBareInput,
} from './formCommon';
import type { ComponentFormProps } from './ComponentFormHost';

/**
 * Phase 6e2a — COIL form, re-cut against components/coil/tmpl/form.tmpl
 * (verify round 1): TWO subtabs only (properties label "Coil" + performance),
 * hidden heading (`rating-mode hide`, no smartlist chrome, Rating block),
 * rated_coil trimmed READ-ONLY variant (rating-selection master_coil fields),
 * paired rows (Type | Duty, Qty High / FH x FL, Rows / FPI, EADB | EAWB,
 * LADB | LAWB, Capacity Sensible | Total with integer thousands separators).
 *
 *  - RatedCoilModel (ahu/rating_mode_coils/<id> — §20 #50) fetched at init →
 *    `draw:coil:connection` = THE G31 pipe feed (JSON-diff gated);
 *  - duties collection (ahu/coils/<id>/duties);
 *  - §49.2 stickies (engine-owned): air_stream_id drags airflow_actual+std;
 *    duty_id ALWAYS re-sent.
 */
const config = COMPONENT_FORM_CONFIGS.coil;

const CoilForm = ({ row, section, section2, cms }: ComponentFormProps) => {
  const api = useComponentForm({
    config,
    row,
    section,
    section2,
    firstSubtab: 'properties',
  });
  const { model } = api;
  // ★ ABOVE any render-path return below -- a hook under one is React #310.
  const streamsFor = useAirStreamRowsFor(cms);
  const emitIfChanged = useJsonDiffEmit();

  const { data: ratedCoilData } = useGetRatedCoilQuery(row.id);
  // The `component.coil.duty.collection` fetch legacy makes at init
  // (coil/view/form.js:218-224). Kept for request parity, and deliberately NOT
  // read: `duties` reaches the template (js:92) but `form.tmpl`'s only two uses
  // of the name (:79, :108) are `{{#hash duties 'id'}}` INSIDE
  // `{{#each componentCms.htfluid}}`, i.e. each HT-fluid row's own `duties`
  // array — wired below, on `cmsList(componentCms,'htfluid')`. See `dutyRows`.
  useGetCoilDutiesQuery(row.id);

  // §33.2 update_rating_dialog:<id> — when syncdata refetches the coil-master
  // and the rated_coil_model / master_coil changes while the rating dialog is
  // open, re-point the open CoilSelector at the fresh models (legacy
  // refetchCoil → Dispatcher.trigger(`update_rating_dialog:${coil_id}`)). We
  // emit on a rated-model diff after the first paint; the selector re-seeds.
  const lastRatedSig = useRef<string | null>(null);
  useEffect(() => {
    if (!ratedCoilData) return;
    const sig = JSON.stringify({
      m: (ratedCoilData as { model?: unknown }).model,
      c: (ratedCoilData as { coil_params?: unknown }).coil_params,
    });
    if (lastRatedSig.current != null && lastRatedSig.current !== sig) {
      ahuBoardBus.emit(
        `update_rating_dialog:${row.id}` as `update_rating_dialog:${number}`
      );
    }
    lastRatedSig.current = sig;
  }, [ratedCoilData, row.id]);

  // G31 feed — rated coil + placement params, JSON-diff gated (parity 319).
  useEffect(() => {
    if (!model || !ratedCoilData) return;
    const payload = {
      rated: ratedCoilData,
      qty_stacked: model.get('qty_stacked'),
      duty_id: model.get('duty_id'),
      h_from_floor: model.get('h_from_floor'),
      mounting_angle: model.get('mounting_angle'),
    };
    emitIfChanged(`coil:${row.id}`, payload, () => {
      ahuBoardBus.emit(
        'draw:coil:connection',
        ratedCoilData,
        section.id,
        Number(model.get('qty_stacked')) || 0,
        Number(model.get('duty_id')) || 0,
        model.get('h_from_floor') as string | number | null,
        model.get('mounting_angle') as string | number | null
      );
    });
  });

  if (api.loading || !model) {
    return <div className="preloader dotted-preloader active" />;
  }
  const sub = (name: string) => !!api.subtabs[name];

  // rated_coil template flag — legacy resolves it from the INIT-fetched
  // RatedCoilModel (rating_mode_coils/<id>) falling back to
  // model.master_coil (coil form.js dataGetterForRendering); a coil with a
  // rated row renders the trimmed RO variant (verify 6e2a-5).
  const rated =
    !!(ratedCoilData && (ratedCoilData as { id?: unknown }).id != null) ||
    !!(model.get('master_coil') as Record<string, unknown> | null)?.id ||
    !!model.get('rate');
  const masterCoil = (model.get('master_coil') ?? {}) as Record<string, unknown>;
  const masterParams = (() => {
    if (masterCoil.params) return masterCoil.params as Record<string, unknown>;
    try {
      return JSON.parse(String(masterCoil.coil_params ?? '{}')) as Record<string, unknown>;
    } catch {
      return {} as Record<string, unknown>;
    }
  })();
  // legacy getCoilType() (coil form.js:276-286) — master_coil.type_value, but
  // only when that rated row carries coil_params; otherwise ''. The gate is
  // load-bearing: a rated fluid coil whose rating-mode row has no coil_params
  // renders none of the fluid-only rows on dev (HT Fluid, Fluid Flow, EFT|LFT,
  // Fluid Velocity, Fouling Factor). Compared raw, as legacy does — coil type
  // `value`s are already lowercase.
  const coilType = masterCoil.coil_params ? String(masterCoil.type_value ?? '') : '';
  const isSteamRated = String(masterParams.type ?? '') === 'Steam';
  // ★ The Duty select is fed by the CMS PARAMS DOC, not by the coil's own duty
  // relation — `form.tmpl:52-56` iterates `componentCms.duty`, and
  // `getSelectedDuty` (form.js:294) resolves the label out of the same list.
  // The port had it on `ahu/coils/<id>/duties/`
  // (`MasterAHUCoilDuty.objects.filter(ahucoil__id=pk)`, duty_viewset.py:23),
  // whose join table `ksdm_ahu_component_coil_duty` is EMPTY — 0 rows on dev3
  // — so the list was always `[]`: Duty rendered blank on every coil and could
  // never show a change, and `selectedDuty` (hence `heating`, which chooses the
  // EADB/EAWB and LADB/LAWB layouts) was permanently ''.
  const dutyRows = cmsList(api.componentCms, 'duty') as OptionRow[];
  const selectedDuty = String(
    // eslint-disable-next-line eqeqeq
    dutyRows.find((d) => d.id == model.get('duty_id'))?.name ?? ''
  );
  const heating = selectedDuty === 'Heating';
  const ro = (value: unknown, extra = 'medium-input') => (
    <input
      type="text"
      className={`rating-selection ${extra}`}
      value={String(value ?? '')}
      readOnly
      onChange={() => undefined}
    />
  );

  return (
    <>
      <FormHeading
        title="coil"
        api={api}
        wrapperClass="component-form-heading clearfix rating-mode hide"
        withRatingBlock
        ratingBlockDataName="coil"
        ratingBlockWrapperClass="ahu-header-btns coil-ahu-header-btns"
      />
      <form onKeyDown={handleTabToNext}>
        <ul className="inputs-group rating-mode max-height-auto" id="coil-form">
          <FormSubtab type="coil" cid={row.id} name="properties" label="Coil" labelHidden open={sub('properties')} onToggle={api.toggleSubtab} sublistClass=" properties">
            <li>
              <div className="single-line">
                {rated ? (
                  <span className="rating-selection rated_coil">{String(model.get('coil_mfg') ?? '')}</span>
                ) : (
                  <PBareSelect api={api} target="master_coil_id" name="mfg_id" cmsPath="manufacturer" />
                )}
                <span className="label">Manufacturer</span>
              </div>
            </li>
            <li>
              <div className="single-line">
                {rated ? (
                  <span className="rating-selection medium-input">{String(masterCoil.type ?? '')}</span>
                ) : (
                  <PBareSelect api={api} target="master_coil_id" name="type_id" cmsPath="type" extraClass="medium-input" />
                )}
                <MBareSelect api={api} name="duty_id" options={dutyRows} extraClass={`duty medium-input${rated ? ' non-clickable' : ''}`} />
                <span className="label">
                  <span>Type</span> | <span>Duty</span>
                </span>
              </div>
            </li>
            {coilType === 'fluid' && (
            <li>
              <div className="heat-transfer-fluid-field single-line">
                <MBareSelect
                  api={api}
                  name="htfluid_id"
                  options={(cmsList(api.componentCms, 'htfluid') as Array<OptionRow & { duties?: Array<{ id: unknown }> }>).filter(
                    (r) =>
                      // eslint-disable-next-line eqeqeq
                      (r.duties ?? []).some((d) => d.id == model.get('duty_id'))
                  )}
                  extraClass="medium-input"
                />
                <span className="label ">HT Fluid</span>
              </div>
            </li>
            )}
            <li>
              <div className="coil-qty-fpi multiply">
                <MBareInput api={api} name="qty_stacked" formatter="integer" extraClass="small-input" />
                <span className="unit unit-slash">/</span>
                <input type="text" className="rating-selection small-input" name="fh" data-formatter_type="decimal" value={formatDecimalDisplay(masterCoil.fh)} readOnly onChange={() => undefined} />
                <span className="multiply-sign"> x </span>
                <input type="text" className="rating-selection small-input" name="fl" data-formatter_type="decimal" value={formatDecimalDisplay(masterCoil.fl)} readOnly onChange={() => undefined} />
                <span className="label">
                  <span>Qty High</span> / <span>FH</span> x <span>FL</span>
                </span>
              </div>
            </li>
            <li>
              <div className="multiply coil-select single-line">
                {rated ? (
                  /* §JSX-WS — `.unit` is `display:inline-block; width:40px`
                     (ahu/styles.css:3483) and the tmpl puts a NEWLINE between
                     every one of these inline-blocks (form.tmpl:168-172), so
                     dev renders a collapsed space before each control. Without
                     them the whole row starts ~4px left of the panel's other
                     inputs, which is the reported misalignment. */
                  <>
                    <span className="unit" />{' '}
                    <input type="text" data-formatter_type="integer" autoComplete="off" className="model_field medium-input" name="rows" value={formatIntegerDisplay(masterCoil.rows)} onChange={() => undefined} />{' '}
                    <span className="unit unit-slash">/</span>{' '}
                    <input type="text" data-formatter_type="decimal" autoComplete="off" className="model_field medium-input" name="fpi" value={formatDecimalDisplay(masterCoil.fpi)} onChange={() => undefined} />
                  </>
                ) : (
                  <>
                    <PBareSelect api={api} target="master_coil_id" name="rows_id" cmsPath="row" labelKey="value" extraClass="medium-input" />
                    <span className="unit unit-slash">/</span>
                    <PBareSelect api={api} target="master_coil_id" name="fpi_id" cmsPath="fpi" labelKey="value" extraClass="medium-input" />
                  </>
                )}
                <span className="label">Rows / FPI</span>
              </div>
            </li>
            <li>
              <div className="coil-diameter-thickness tube">
                {/* §JSX-WS, as above — form.tmpl:178-190. */}
                <span className="unit unit-bottom">in</span>{' '}
                {rated && (
                  <>
                    {ro(masterCoil.tube_dia)}{' '}
                    {ro(masterCoil.tube_wall_thick)}
                  </>
                )}
                <span className="label">Tube Diameter / Thickness</span>
              </div>
            </li>
            <li>
              <div className="coil-diameter-thickness tube">
                {/* §JSX-WS, as above — form.tmpl:192-203. */}
                <span className="unit" />{' '}
                {rated && ro(masterCoil.tube_mtl)}
                <span className="label">Tube Material</span>
              </div>
            </li>
            <li>
              <div className="single-line">
                {rated ? (
                  <span className="rating-selection overflow-hidden">
                    {String(masterCoil.circuiting ?? masterCoil.circuiting_rating_output ?? '')}
                  </span>
                ) : (
                  <PBareSelect api={api} target="master_coil_id" name="circuiting_id" cmsPath="circuiting" />
                )}
                <span className="label">
                  <span>Circuiting</span>
                </span>
              </div>
            </li>
            <li>
              <div>
                {rated && (
                  <>
                    {ro(masterCoil.fin_thick)}
                    {ro(masterParams.fin_pattern, 'medium-input text-left')}
                  </>
                )}
                <span className="label">Fin Thickness / Pattern</span>
              </div>
            </li>
            <li>
              <div>
                {rated && ro(masterCoil.fin_mtl)}
                <span className="label">Fin Material</span>
              </div>
            </li>
            <li>
              <div className="single-line">
                {rated ? (
                  <span className="rating-selection">{String(masterCoil.casing_mtl ?? '')}</span>
                ) : (
                  <PBareSelect api={api} target="master_coil_id" name="casing_mtl_id" cmsPath="casing_mtl" />
                )}
                <span className="label">Casing Material</span>
              </div>
            </li>
            <li>
              <div className="single-line">
                {rated && <span className="rating-selection">{String(masterCoil.fin_finish ?? '')}</span>}
                <span className="label">Finish</span>
              </div>
            </li>
            {!rated && (
              <>
                <PSelect api={api} target="master_coil_id" name="casing_type_id" label="Type" cmsPath="casing_type" />
                <PSelect api={api} target="master_coil_id" name="conn_mtl_id" label="Connection Material" cmsPath="conn_mtl" />
                <PSelect api={api} target="master_coil_id" name="header_mtl_id" label="Header Material" cmsPath="header_mtl" />
                <PSelect api={api} target="master_coil_id" name="conn_pipe_end_id" label="Pipe End" cmsPath="conn_pipe_end" />
                <PSelect api={api} target="master_coil_id" name="condensate_conn_size_id" label="Condensate Conn Size" cmsPath="condensate_conn_size" labelKey="value" />
                <PSelect api={api} target="master_coil_id" name="vent_drain_id" label="Vent & Drain Side" cmsPath="vent_drain" />
                <PSelect api={api} target="master_coil_id" name="tube_interior_id" label="Tube Interior" cmsPath="tube_interior" />
              </>
            )}
          </FormSubtab>

          <FormSubtab type="coil" cid={row.id} name="performance" label="Performance" open={sub('performance')} onToggle={api.toggleSubtab}>
            <li>
              <div className="single-line">
                <MBareSelect
                  api={api}
                  name="air_stream_id"
                  options={streamsFor(model.get('air_stream_id'))}
                  extraClass={`medium-input coli-medium ${rated ? 'non-clickable' : 'clickable'}`}
                />
                <span className="unit long-unit">CFM</span>
                <MBareInput api={api} name="airflow_actual" formatter="integer" extraClass="medium-input coli-medium" />
                <span className="label">Air Stream &amp; Flow</span>
              </div>
            </li>
            <li>
              <div className="total-airflow">
                <span className="unit">fpm</span>
                <MBareInput api={api} name="face_velocity_actual" readOnly extraClass="medium-input coli-medium" />
                <span className="unit long-unit">in WC</span>
                <MBareInput api={api} name="apd" extraClass="medium-input coli-medium" />
                <span className="label">
                  <span>Face Velocity</span> | <span>APD</span>
                </span>
              </div>
            </li>
            <li>
              <div className="coil-efficiency-mech">
                <span className="unit temp"> F </span>
                <SBareInput api={api} name="eadb_temp" value={section.eadb_temp} />
                {!heating && <SBareInput api={api} name="eawb_temp" value={section.eawb_temp} />}
                <span className="label">
                  <span>EADB</span>
                  {!heating && (
                    <>
                      {' | '}
                      <span>EAWB</span>
                    </>
                  )}
                </span>
              </div>
            </li>
            <li>
              <div className="coil-efficiency-mech">
                <span className="unit temp"> F </span>
                <MBareInput api={api} name="ladb_temp" extraClass="medium-input" />
                {!heating && <MBareInput api={api} name="lawb_temp" extraClass="medium-input" />}
                <span className="label">
                  <span>LADB</span>
                  {!heating && (
                    <>
                      {' | '}
                      <span>LAWB</span>
                    </>
                  )}
                </span>
              </div>
            </li>
            <li>
              <div className="coil-efficiency-mech">
                <span className="unit">btu/hr</span>
                <MBareInput api={api} name="capacity_sensible_all" formatter="integer" extraClass="medium-input" />
                <MBareInput api={api} name="desired_capacity_total" valueName="capacity_total_all" formatter="integer" extraClass="medium-input" />
                <span className="label">
                  <span>Capacity Sensible</span> | <span>Total</span>
                </span>
              </div>
            </li>
            {coilType === 'fluid' && (
              <li>
                <div>
                  <span className="unit">gpm</span>
                  <MBareInput api={api} name="fluid_flow" extraClass="medium-input" />
                  <span className="unit unit-slash">/</span>
                  <MBareInput api={api} name="fluid_flow_total" extraClass="medium-input" />
                  <span className="label">Fluid Flow</span>
                </div>
              </li>
            )}
            {coilType === 'fluid' && (
              <li>
                <div>
                  <span className="unit temp"> F </span>
                  <MBareInput api={api} name="entering_fluid_temp" extraClass="medium-input " />
                  <MBareInput api={api} name="leaving_fluid_temp" extraClass="medium-input " />
                  <span className="label">
                    <span>EFT</span> | <span>LFT</span>
                  </span>
                </div>
              </li>
            )}
            {coilType === 'evaporator' && (
              <li>
                <div className="frequency">
                  <span className="unit">lb</span>
                  <MBareInput api={api} name="refrigerant_charge" />
                  <span className="label">Refrigerant Charge</span>
                </div>
              </li>
            )}
            {coilType === 'evaporator' && (
              <li>
                <div className="single-line">
                  <span className="selection">
                    {String((model.get('evaporator_mode') as { name?: string } | null)?.name ?? '')}
                  </span>
                  <select
                    name="evaporator_mode_id"
                    className="coil_params_field"
                    required
                    value={String(api.catalogueFields.master_coil_id?.paramValue('evaporator_mode_id') ?? 'None')}
                    onChange={(e) =>
                      api.commitParamsField('master_coil_id', 'evaporator_mode_id', e.target.value)
                    }
                  >
                    <option value="None">- -</option>
                    {cmsList(api.componentCms, 'evaporator_mode').map((r) => (
                      <option key={String(r.id)} value={String(r.id)}>
                        {String(r.name ?? '')}
                      </option>
                    ))}
                  </select>
                  <span className="label">Evaporator Mode</span>
                </div>
              </li>
            )}
            {(coilType === 'evaporator' || coilType === 'condenser') && (
              <li>
                <div>
                  <span className="unit temp"> F </span>
                  <MBareInput api={api} name="suction_temp" extraClass="medium-input coli-medium" />
                  <span className="unit long-unit" />
                  <MBareInput api={api} name="liquid_ref_temp" extraClass="medium-input coli-medium" />
                  <span className="label">
                    <span>SST</span> | <span>Liquid Ref. Temp</span>
                  </span>
                </div>
              </li>
            )}
            {isSteamRated && (
              <MInput api={api} name="entering_superheat" label="Entering Superheat" unit="F" />
            )}
            {coilType === 'fluid' && (
              <li>
                <div className="">
                  <span className="unit">fps</span>
                  <MBareInput api={api} name="fluid_velocity" extraClass="medium-input coli-medium" />
                  <span className="label">
                    <span>Fluid Velocity</span>
                  </span>
                </div>
              </li>
            )}
            {coilType === 'evaporator' && (
              <li>
                <div className="multiply">
                  <span className="unit">fps</span>
                  <MBareInput api={api} name="tube_vapor_velocity" />
                  <span className="label">Tube Vapor Velocity</span>
                </div>
              </li>
            )}
            {!isSteamRated && (
              <li>
                <div className="">
                  <span className="unit">ft WC</span>
                  <MBareInput api={api} name="fluid_pd" extraClass="medium-input coli-medium" />
                  <span className="label">
                    <span>Fluid Pressure Drop</span>
                  </span>
                </div>
              </li>
            )}
            {isSteamRated && <MInput api={api} name="steam_pressure" label="Steam Pressure" unit="PSI" />}
            {isSteamRated && (
              <MInput api={api} name="condensate_load_per_circuit" label="Condensate" unit="lb/hr" />
            )}
            {coilType === 'fluid' && (
              <li>
                <div className="single-line">
                  <span className="unit ft-unit">ft2 Fhr/btu</span>
                  <MBareInput api={api} name="fouling_factor_inside" extraClass="medium-input" />
                  <MBareInput api={api} name="fouling_factor_outside" extraClass="medium-input" />
                  <span className="label">Fouling Factor In / Out</span>
                </div>
              </li>
            )}
          </FormSubtab>

          <BlankOffSlot api={api} sectionId={section.id} />
        </ul>
      </form>
      {/* components/coil/tmpl/form.tmpl:933 — the footer note closes EVERY
          legacy component form; the coil port dropped it. */}
      <RequiredFieldNote />
    </>
  );
};

export default CoilForm;
